import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Clock, Target, Users, TrendingUp } from "lucide-react";
import { useState, useEffect } from "react";

const CURRENCY_SYMBOLS: Record<string, string> = {
  INR: '₹',
  USD: '$',
  EUR: '€',
  GBP: '£',
};

interface FundingProgressHeaderProps {
  beneficiaryName?: string | null;
  targetAmount: number;
  collectedAmount: number;
  currency: string;
  paidCount: number;
  pendingCount: number;
  perPersonAmount?: number | null;
  showTicker?: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function FundingProgressHeader({
  beneficiaryName,
  targetAmount,
  collectedAmount,
  currency,
  paidCount,
  pendingCount,
  perPersonAmount,
  showTicker = true,
}: FundingProgressHeaderProps) {
  const currencySymbol = CURRENCY_SYMBOLS[currency] || currency;
  const progressPercent = targetAmount > 0 ? Math.min(100, (collectedAmount / targetAmount) * 100) : 0;
  const pendingAmount = targetAmount - collectedAmount;
  
  // Animated ticker effect
  const [displayedAmount, setDisplayedAmount] = useState(0);
  
  useEffect(() => {
    if (!showTicker) {
      setDisplayedAmount(collectedAmount);
      return;
    }
    
    // Animate from 0 to collected amount
    const duration = 1500; // 1.5 seconds
    const steps = 60;
    const increment = collectedAmount / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= collectedAmount) {
        setDisplayedAmount(collectedAmount);
        clearInterval(timer);
      } else {
        setDisplayedAmount(Math.floor(current));
      }
    }, duration / steps);
    
    return () => clearInterval(timer);
  }, [collectedAmount, showTicker]);

  return (
    <div className="space-y-4">
      {/* Beneficiary Name */}
      {beneficiaryName && (
        <div className="flex items-center gap-2 text-lg">
          <Target className="w-5 h-5 text-primary" />
          <span className="font-medium">For</span>
          <span className="font-bold text-primary">{beneficiaryName}</span>
        </div>
      )}
      
      {/* Main Amount Display - GoFundMe Style */}
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-baseline gap-1">
          <span className="text-4xl font-bold text-primary">
            {currencySymbol}{displayedAmount.toLocaleString()}
          </span>
          <span className="text-muted-foreground text-lg">
            raised of {currencySymbol}{targetAmount.toLocaleString()} goal
          </span>
        </div>
        <Badge variant="secondary" className="text-base px-3 py-1">
          {progressPercent.toFixed(0)}%
        </Badge>
      </div>
      
      {/* Progress Bar */}
      <div className="relative">
        <Progress value={progressPercent} className="h-4 rounded-full" />
        {progressPercent > 0 && progressPercent < 100 && (
          <div 
            className="absolute top-0 h-4 flex items-center justify-end pr-1"
            style={{ width: `${Math.max(progressPercent, 5)}%` }}
          >
            <TrendingUp className="w-3 h-3 text-primary-foreground animate-pulse" />
          </div>
        )}
      </div>
      
      {/* Stats Row */}
      <div className="flex items-center justify-between flex-wrap gap-4 text-sm">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-accent" />
            </div>
            <div>
              <div className="font-semibold text-accent">{paidCount}</div>
              <div className="text-xs text-muted-foreground">paid</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-destructive/10 flex items-center justify-center">
              <Clock className="w-4 h-4 text-destructive" />
            </div>
            <div>
              <div className="font-semibold text-destructive">{pendingCount}</div>
              <div className="text-xs text-muted-foreground">pending</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-primary" />
            </div>
            <div>
              <div className="font-semibold text-primary">{paidCount + pendingCount}</div>
              <div className="text-xs text-muted-foreground">total</div>
            </div>
          </div>
        </div>
        
        {/* Pending Amount & Per Person */}
        <div className="flex flex-col items-end gap-1">
          {pendingAmount > 0 && (
            <div className="text-muted-foreground">
              <span className="font-medium text-foreground">{currencySymbol}{pendingAmount.toLocaleString()}</span>
              {" "}still needed
            </div>
          )}
          {perPersonAmount && perPersonAmount > 0 && (
            <Badge variant="outline" className="text-xs">
              {currencySymbol}{perPersonAmount.toFixed(0)}/person
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
};
